import React, { useState } from 'react';
import { Container, Box, Typography, TextField, Button, Alert, Paper, Link as MuiLink } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser, loginUser, sendOtp, otpErrorMessage } from '../api/apiClient';
import { setToken } from '../api/session';
import { setWorkflowOwner } from '../mock/localWorkflowStore';
import PasswordVisibilityToggle from '../components/PasswordVisibilityToggle';

function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\+?[1-9]\d{9,14}$/;
    return phoneRegex.test(phone);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email first.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await sendOtp(email);
      setOtpSent(true);
      setSuccess('OTP sent to your email.');
    } catch (err) {
      setError(otpErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    if (!validatePhoneNumber(phone)) {
      setError('Please enter a valid phone number (e.g., +1234567890 or 1234567890)');
      setLoading(false);
      return;
    }
    
    if (!otpSent) {
      setError('Please send and enter the OTP first.');
      setLoading(false);
      return;
    }

    try {
      await registerUser(name, email, phone, password, otp);
      // Auto login after registration
      const data = await loginUser(email, password);
      setToken(data.access_token);
      setWorkflowOwner(email.trim().toLowerCase());
      navigate('/build');
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight="bold">
            Sign Up for OnePass
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Box component="form" onSubmit={otpSent ? handleRegister : handleSendOtp} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="phone"
              label="Phone Number"
              name="phone"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={otpSent}
              InputProps={{
                endAdornment: (
                  <PasswordVisibilityToggle
                    visible={showPassword}
                    onToggle={() => setShowPassword((prev) => !prev)}
                  />
                ),
              }}
            />
            {otpSent && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="otp"
                label="Enter OTP"
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? (otpSent ? 'Creating Account...' : 'Sending OTP...') : (otpSent ? 'Sign Up' : 'Send OTP')}
            </Button>
            <Box textAlign="center">
              <Typography variant="body2">
                Already have an account?{' '}
                <MuiLink component={Link} to="/build/login" variant="body2">
                  Sign In
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default RegisterPage;
